import { type FC } from "react";

import { type DocumentFragment, type ProjectFragment } from "@app_schema";

import { EmptyPlaceholder } from "@styled/empty_placeholder";
import { FileManager } from "@styled/file_manager";
import { FilePill } from "@styled/file_pill";
import { FileQueue } from "@styled/file_queue";
import { Stack } from "@styled/stack";

import { IntakePane } from "./intake_pane";
import { UploadButton, renderFileQueueItem } from "./shared_card_utils";

// Supporting documents bucket (any other case files), hosted in the
// guided-intake pane. File-list + upload internals reused verbatim from the
// prior SupportingDocumentsCard; only the StageCard chrome is swapped for the
// flat IntakePane header + approved copy.
export const SupportingDocumentsPane: FC<{
  project: ProjectFragment;
  documents: DocumentFragment[];
  save: (blob: { signed_id: string }) => Promise<void>;
}> = ({ project, documents, save }) => {
  const fileCount = documents.length;

  return (
    <FileManager save={save}>
      <IntakePane
        title="Supporting documents"
        subtitle="Any other files for the case."
        controls={
          project.policy.modify ? (
            <UploadButton variant={fileCount > 0 ? "add" : "upload"} />
          ) : undefined
        }
      >
        {fileCount === 0 && (
          <EmptyPlaceholder
            title="No supporting documents"
            text="Upload any additional files for this case"
          />
        )}

        <Stack>
          <FileQueue render={renderFileQueueItem} />

          {documents.map((document) => (
            <FilePill
              key={document.id}
              name={document.name}
              type={document.file.type}
              size={document.file.bytesize}
              to={`/dashboard/projects/${project.id}/documents/${document.id}`}
            />
          ))}
        </Stack>
      </IntakePane>
    </FileManager>
  );
};
